import { useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import type { OverlayPanel } from "primereact/overlaypanel";

import type { SavedFilter } from "@/ds";
import {
  DELETE_CONFIRM_LABEL,
  DELETE_DIALOG_TITLE,
  DROPDOWN_OPEN_SELECTOR,
  FILTERS_LABEL,
  TRIGGER_MOBILE_CLASS,
} from "./filters-popover.constants";
import type { FiltersPopoverProps, FiltersPopoverViewModel } from "./types-filters-popover";

/** ViewModel do `FiltersPopover`: contador, rótulos, abertura do painel, salvar e excluir filtro salvo; sem JSX. */
export function useFiltersPopover({
  definitions,
  value,
  onChange,
  savedFilters,
  onSaveFilter,
  onDeleteSavedFilter,
  onApplySavedFilter,
  isMobile = false,
  showClear = true,
  testId,
}: FiltersPopoverProps): FiltersPopoverViewModel {
  const overlay = useRef<OverlayPanel>(null);
  const [aberto, setAberto] = useState(false);
  const [salvarAberto, setSalvarAberto] = useState(false);
  const [exclusaoPendente, setExclusaoPendente] = useState<SavedFilter | null>(null);

  // O Esc fecha o painel só quando nenhum dropdown de valores está aberto (o do próprio dropdown) e o diálogo de
  // exclusão não está na frente (o Esc é dele). O listener é de captura: roda antes de o dropdown se fechar, e o
  // `closeOnEscape` do OverlayPanel, que fecharia os dois de uma vez, fica desligado.
  useEffect(() => {
    if (!aberto) return;

    function fecharComEsc(event: KeyboardEvent) {
      if (event.key !== "Escape" || exclusaoPendente !== null) return;
      if (document.querySelector(DROPDOWN_OPEN_SELECTOR)) return;
      overlay.current?.hide();
    }

    document.addEventListener("keydown", fecharComEsc, true);
    return () => document.removeEventListener("keydown", fecharComEsc, true);
  }, [aberto, exclusaoPendente]);

  // O Prime posiciona o painel só ao abrir: se ele cresce depois (linha de Data em período), realinha para não passar da tela.
  useEffect(() => {
    const element = aberto ? overlay.current?.getElement() : null;
    if (!element || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => overlay.current?.align());
    observer.observe(element);
    return () => observer.disconnect();
  }, [aberto]);

  const ativos = definitions.filter(({ key }) => (value[key] ?? []).length > 0).length;

  return {
    overlay,
    root: {
      definitions,
      value,
      onChange,
      testId,
      showClear,
      /** Só o clique em "Aplicar" fecha o painel; Limpar e Desfazer consultam na hora e o mantêm aberto. */
      onApply: () => overlay.current?.hide(),
    },
    fieldTestId: `${testId}-field`,
    trigger: {
      testId,
      label: FILTERS_LABEL,
      badge: ativos > 0 ? String(ativos) : undefined,
      ariaLabel: ativos > 0 ? `${FILTERS_LABEL} (${ativos} aplicados)` : FILTERS_LABEL,
      expanded: aberto,
      className: isMobile ? TRIGGER_MOBILE_CLASS : undefined,
      size: isMobile ? ("small" as const) : undefined,
      alternar: (event: MouseEvent) => overlay.current?.toggle(event),
    },
    panel: {
      abrir: () => setAberto(true),
      fechar: () => setAberto(false),
    },
    /** Sem `savedFilters` e `onSaveFilter`, a tela não oferece filtros salvos. */
    saved:
      savedFilters !== undefined && onSaveFilter !== undefined
        ? {
            savedFilters,
            onSelect: onApplySavedFilter,
            pedirExclusao: onDeleteSavedFilter
              ? (id: string) =>
                  setExclusaoPendente(savedFilters.find((saved) => saved.id === id) ?? null)
              : undefined,
            abrirSalvar: () => setSalvarAberto(true),
            fecharSalvar: () => setSalvarAberto(false),
            salvarAberto,
            salvar: onSaveFilter,
            exclusao: {
              visible: exclusaoPendente !== null,
              header: DELETE_DIALOG_TITLE,
              message: `O filtro "${exclusaoPendente?.name ?? ""}" será excluído e não poderá ser recuperado.`,
              acceptLabel: DELETE_CONFIRM_LABEL,
              testId: `${testId}-delete`,
              confirmar: () => {
                if (exclusaoPendente) onDeleteSavedFilter?.(exclusaoPendente.id);
              },
              cancelar: () => setExclusaoPendente(null),
            },
            /** O diálogo mora fora do painel: sem isto o clique nele seria "fora" para o painel e o fecharia por trás. */
            isolarClique: (event: MouseEvent) => event.stopPropagation(),
          }
        : null,
  };
}
